import {Keycap} from '@dropbox/dig-components/keycap';
import {Truncate} from '@dropbox/dig-components/truncate';
import {Typeahead} from '@dropbox/dig-components/typeahead';
import {Text} from '@dropbox/dig-components/typography';
import {Split} from '@dropbox/dig-foundations';
import {IS_APPLE} from '@lexical/utils';
import type {SpeculativeProgress} from 'components/ai/speculativeAsk';
import {AvatarIcon} from 'components/DSYS/Avatar';
import {CompanyAskGlyph} from 'components/DSYS/CompanyOSLogo';
import {t} from 'i18next';

import styles from './PeopleSearch.module.css';

const ENTER = {symbol: 'return', withAccessibleLabel: 'Enter'} as const;
const MOD = {
  symbol: IS_APPLE ? 'command' : 'control',
  withAccessibleLabel: IS_APPLE ? 'Command' : 'Control',
} as const;

/**
 * The Ask row, sized like a result. When Ask leads (a question-shaped query, or
 * no results) it comes first and is the target of plain Enter; with Ask already
 * started on the question (`progress`), it shows the latest step, then the
 * answer's first sentence. `trailing` is the lookup form ("Kyle"): after the
 * results, so the person comes first, reading "Ask about “Kyle”" with ⌘↵.
 */
export const SearchAskQuestionRow = ({
  query,
  progress,
  trailing = false,
}: {
  query: string;
  progress?: SpeculativeProgress | null;
  trailing?: boolean;
}) => (
  <Typeahead.Row
    key={trailing ? 'company-os-ai-ask-trailing' : 'company-os-ai-ask-question'}
    value={{
      route: 'company_os_ai',
      path: query,
      label: query,
      index: -1,
    }}
    withTitle={
      <Split alignY="center" gap="8">
        <Split.Item width="fill" style={{minWidth: 0}}>
          <Text isBold>
            <Truncate lines={1} maxWidth={700}>
              {trailing ? t('search_ask_about', {query}) : query}
            </Truncate>
          </Text>
        </Split.Item>
        <Split.Item width="hug">
          <Keycap.Group
            display="inline"
            variant="outline"
            size="small"
            withAccessibleAdjacentLabel="and"
            keycaps={trailing ? [MOD, ENTER] : [ENTER]}
          />
        </Split.Item>
      </Split>
    }
    withSubtitle={
      <Text size="small" color="faint">
        {/* The answer gets a second line, so the row grows once when it starts. */}
        <Truncate lines={progress?.preview ? 2 : 1} maxWidth={700}>
          {progress?.preview ??
            (progress?.step ? `${t('ai_search_ask')} • ${progress.step}` : t('ai_search_ask'))}
        </Truncate>
      </Text>
    }
    className={styles.typeaheadRow}
    withLeftAccessory={<AvatarIcon src={CompanyAskGlyph} size="large" />}
  />
);
